/**
 * Flow Optimizer Tag Rules — CRUD hooks (ranking tags for paths/landers/offers).
 * The computed tags themselves: useFlowOptimizerTags.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FlowLayer, FlowTagCondition, FlowTagRule } from '@trafficpulse/shared';

import api from '@/services/api';

const BASE = '/smswiser/flow-optimizer-tag-rules';

export interface CreateFlowTagRuleInput {
  layer: FlowLayer;
  name: string;
  color?: string;
  emoji?: string;
  conditions: FlowTagCondition[];
  dateRangePreset?: string;
  fromDate?: string;
  toDate?: string;
  toDateIsToday?: boolean;
  enabled?: boolean;
  groupKey?: string;
}

export type UpdateFlowTagRuleInput = Partial<Omit<CreateFlowTagRuleInput, 'layer'>>;

const keyFor = (layer: FlowLayer) => ['smswiser', 'flow-tag-rules', layer] as const;

/** Invalidate the flow tags shown on stats pages + preview after rules change. */
function invalidateFlowTags(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) && q.queryKey.includes('flow-optimizer-tags'),
  });
}

/** `enabled=false` skips the fetch — for hosts that only sometimes render the
 *  rules (the bank filter bar's performance-tag select). The endpoint is
 *  permission-gated, so callers must treat missing data as "no catalogue",
 *  not an error state. */
export function useFlowTagRules(layer: FlowLayer, enabled = true) {
  return useQuery({
    queryKey: keyFor(layer),
    enabled,
    // The ⚡ ranking chips on the Flows overlay are derived client-side from
    // these rules (+ rule-independent raw scores). Keep them always-fresh so a
    // rule edited on the admin page shows up the moment you revisit/refresh the
    // Flows page — the list is tiny, so the extra GET is cheap. (The expensive
    // server-computed stats tags, useFlowOptimizerTags, stay cached at 60s.)
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const res = await api.get<{ data: FlowTagRule[] }>(BASE, {
        params: { layer },
      });
      return Array.isArray(res.data?.data) ? res.data.data : [];
    },
  });
}

export function useCreateFlowTagRule(layer: FlowLayer) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateFlowTagRuleInput) => {
      const res = await api.post<{ data: FlowTagRule }>(BASE, dto);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyFor(layer) });
      invalidateFlowTags(qc);
    },
  });
}

export function useUpdateFlowTagRule(layer: FlowLayer) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateFlowTagRuleInput }) => {
      const res = await api.patch<{ data: FlowTagRule }>(`${BASE}/${id}`, dto);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyFor(layer) });
      invalidateFlowTags(qc);
    },
  });
}

export function useDeleteFlowTagRule(layer: FlowLayer) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${BASE}/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyFor(layer) });
      invalidateFlowTags(qc);
    },
  });
}

export function useBulkDeleteFlowTagRules(layer: FlowLayer) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api.delete<{ deleted: number }>(`${BASE}/bulk`, {
        data: { ids },
      });
      return res.data?.deleted ?? 0;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyFor(layer) });
      invalidateFlowTags(qc);
    },
  });
}

export function useReorderFlowTagRules(layer: FlowLayer) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Array<{ id: string; orderIndex: number }>) => {
      const res = await api.patch<{ data: { updated: number } }>(`${BASE}/reorder`, {
        updates,
      });
      return res.data?.data ?? { updated: 0 };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keyFor(layer) });
      invalidateFlowTags(qc);
    },
  });
}
